import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';
import { api, SERVER_ORIGIN } from '../services/api';
import { textsPath } from '../utils/textsLink';
import ReminderAdd from '../components/ReminderAdd';
import TodoSheet from '../components/TodoSheet';
import { toast } from 'react-toastify';
import {
  Loader, Check, Phone, MessageSquare, Plus, ChevronRight, AlertCircle,
  Users, FileSignature, ListChecks, Sun, Moon, Backpack, Play, Map as MapIcon, ClipboardCheck, Bell } from 'lucide-react';

/**
 * My Day — the owner's phone screen.
 *
 * His words (Sep 16 2026): "Make a special login that is for me that has all
 * the quotes for the day, all the to-do list for today, all the morning
 * checklist to do, all the after-mow checklist to do, and then has options to
 * see how the crew is doing — just kind of for the phone. Everything that's
 * like not admin, but can make a customer, can make a quote, can make a job,
 * can make a to-do list. Can adjust things on the fly."
 *
 * One screen, one column, big taps. Backed by GET /api/day/owner.
 */

function clock(hhmm?: string | null) {
  if (!hhmm) return null;
  const [h, m] = String(hhmm).split(':').map(Number);
  if (!Number.isFinite(h)) return null;
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m ?? 0).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
}

function since(ts?: string | null) {
  if (!ts) return null;
  const t = new Date(String(ts).replace(' ', 'T') + (String(ts).endsWith('Z') ? '' : 'Z')).getTime();
  if (!Number.isFinite(t)) return null;
  const mins = Math.round((Date.now() - t) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  return `${Math.round(mins / 6) / 10} h ago`;
}

function Box({ title, icon: Icon, right, children }: {
  title: string; icon?: React.ElementType; right?: React.ReactNode; children: React.ReactNode;
}) {
  return (
    <section className="bg-white rounded-2xl border border-gray-200 p-4">
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon className="h-4 w-4 text-gray-500" />}
        <h2 className="text-xs font-black uppercase tracking-wider text-gray-600 flex-1">{title}</h2>
        {right}
      </div>
      {children}
    </section>
  );
}

/**
 * The page at /my-day. Since Nov 2 2026 the same blocks sit on the one morning
 * page (pages/Today) under their own header, so the blocks are `MyDayBlocks`
 * and this page only renders them whole.
 */
export default function MyDay() {
  return <MyDayBlocks />;
}

/**
 * My Day as blocks. `embedded` is the one-morning-page mode (pages/Today):
 * the day banner, the Needs Attention link, the Quote visits box and the
 * Manager check box are left off, because each of those is a block of its
 * own on that page — the brief's note stays, as one line. Nothing else changes.
 */
export function MyDayBlocks({ embedded = false }: { embedded?: boolean }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [todoText, setTodoText] = useState('');
  // A to-do with a client and an address (components/TodoSheet, services/todos; owner, Oct 26 2026).
  const [todoSheet, setTodoSheet] = useState(false);
  const todoDone = useMutation((id: number) => api.post(`/todos/${id}/done`).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d?.message || 'Done'); qc.invalidateQueries('my-day'); qc.invalidateQueries('my-day-attention'); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not mark that done'); },
  });
  const reminderDone = useMutation((id: number) => api.post(`/reminders/${id}/done`).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d.message || 'Done'); qc.invalidateQueries('my-day'); qc.invalidateQueries('attention'); },
  });
  const reminderSnooze = useMutation((id: number) => api.post(`/reminders/${id}/snooze`, { when: 'week' }).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d.message || 'Pushed a week'); qc.invalidateQueries('my-day'); qc.invalidateQueries('attention'); },
  });

  const { data, isLoading } = useQuery('my-day', () => api.get('/day/owner').then(r => r.data), {
    refetchInterval: 120000,
  });
  const { data: attention } = useQuery('my-day-attention', () => api.get('/attention').then(r => r.data), {
    refetchInterval: 300000,
  });
  // Texts that asked for something nobody has finished (server/services/textAsks): the one line, and the tap that finishes it.
  const { data: askData } = useQuery('my-day-asks', () => api.get('/messages/asks').then(r => r.data), { refetchInterval: 120000 });
  const openAsks: any[] = (askData?.asks || []).filter((a: any) => a.status === 'open');
  const askHandled = useMutation(({ id, note }: { id: number; note: string | null }) => api.post(`/messages/asks/${id}/handled`, { note }).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d?.message || 'Marked handled'); qc.invalidateQueries('my-day-asks'); qc.invalidateQueries('my-day-attention'); qc.invalidateQueries('attention'); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not mark that'); },
  });
  const askDone = (a: any) => {
    const note = window.prompt(`How was ${a.who || 'this'} taken care of? (optional)`, '');
    if (note === null) return;
    askHandled.mutate({ id: a.id, note: note.trim() || null });
  };

  const tick = useMutation(
    (v: { list: string; item: string; on: boolean }) => api.post('/day/check', v).then(r => r.data),
    {
      onSuccess: (d: any) => {
        qc.setQueryData('my-day', (old: any) => (old ? { ...old, checklists: d.checklists } : old));
      },
      onError: () => { toast.error('Could not save that tick'); },
    }
  );

  const addTodo = useMutation(
    (text: string) => api.post('/attention/todo', { text }).then(r => r.data),
    {
      onSuccess: () => { setTodoText(''); toast.success('On the list'); qc.invalidateQueries('my-day-attention'); },
      onError: () => { toast.error('Could not add that'); },
    }
  );

  if (isLoading) {
    return <div className="py-20 flex justify-center"><Loader className="h-6 w-6 animate-spin text-brand-600" /></div>;
  }
  if (!data) return <p className="text-sm text-gray-500">Could not load today.</p>;

  const brief = data.brief || {};
  const crew = data.crew || {};
  const pack: any[] = brief.pack || [];
  const attentionCount = attention?.counts?.total ?? null;
  const doFirst = (attention?.groups || []).find((g: any) => g.key === 'now')?.items || [];

  return (
    <div className="space-y-4 max-w-2xl mx-auto pb-8">
      {/* The day in a sentence */}
      {embedded ? (
        brief.note ? <p className="text-[15px] font-semibold text-gray-900 leading-relaxed px-1">{brief.note}</p> : null
      ) : (
        <section className="rounded-3xl bg-brand-600 text-black shadow-brand p-5">
          <h1 className="text-3xl font-black uppercase tracking-tight leading-none">{data.day_name}</h1>
          {brief.note && <p className="mt-2 text-[15px] font-semibold leading-relaxed">{brief.note}</p>}
        </section>
      )}

      {/* Make something, right now */}
      <div className="grid grid-cols-2 gap-2">
        <button onClick={() => navigate('/calendar?quote=1')}
          className="press py-3.5 rounded-2xl bg-brand-600 shadow-brand text-black text-sm font-bold flex items-center justify-center gap-2">
          <Plus className="h-4 w-4" /> Quote visit
        </button>
        <button onClick={() => navigate('/jobs?new=1')}
          className="press py-3.5 rounded-2xl border-2 border-gray-200 bg-white shadow-soft text-gray-900 text-sm font-bold flex items-center justify-center gap-2 hover:border-brand-300">
          <Plus className="h-4 w-4" /> Job
        </button>
        <button onClick={() => navigate('/customers?new=1')}
          className="press py-3.5 rounded-2xl border-2 border-gray-200 bg-white shadow-soft text-gray-900 text-sm font-bold flex items-center justify-center gap-2 hover:border-brand-300">
          <Users className="h-4 w-4" /> Customer
        </button>
        <button onClick={() => navigate('/quotes?new=1')}
          className="press py-3.5 rounded-2xl border-2 border-gray-200 bg-white shadow-soft text-gray-900 text-sm font-bold flex items-center justify-center gap-2 hover:border-brand-300">
          <FileSignature className="h-4 w-4" /> Bid
        </button>
      </div>

      {/* Jobs today that aren't on a route: one tap to route them. */}
      {Number(data.loose_jobs) > 1 && (
        <button onClick={() => navigate(`/calendar?route_day=${data.day}`)}
          className="w-full text-left bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center gap-3">
          <MapIcon className="h-5 w-5 text-amber-700" />
          <span className="flex-1">
            <span className="block text-sm font-semibold text-amber-900">{data.loose_jobs} jobs today aren't on a route</span>
            <span className="block text-xs text-amber-800">Route them — in driving order, promised times kept</span>
          </span>
          <ChevronRight className="h-4 w-4 text-amber-600" />
        </button>
      )}

      {/* What has to happen before anything else (its own block on the one morning page) */}
      {!embedded && attentionCount != null && (
        <Link to="/attention" className="block bg-white rounded-2xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <AlertCircle className={`h-5 w-5 ${doFirst.length ? 'text-red-500' : 'text-gray-500'}`} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900">
                {doFirst.length ? `${doFirst.length} to do first` : `${attentionCount} on the list`}
              </p>
              {doFirst[0] && <p className="text-xs text-gray-500 truncate">{doFirst[0].title}</p>}
            </div>
            <ChevronRight className="h-4 w-4 text-gray-300" />
          </div>
        </Link>
      )}

      {/* Texts that asked for something and are not finished yet */}
      {openAsks.length > 0 && (
        <Box title="Texts waiting on you" icon={MessageSquare} right={<Link to="/texts" className="text-xs font-semibold text-brand-700">All texts</Link>}>
          <ul className="space-y-2">
            {openAsks.slice(0, 8).map((a: any) => (
              <li key={a.id} className="rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-3">
                <p className="text-sm font-semibold text-gray-900">{a.line}</p>
                <p className="text-xs text-gray-600 mt-0.5">{a.summary}</p>
                <div className="mt-2 flex gap-2">
                  {a.kind === 'quote' && (
                    <button onClick={() => navigate(a.customer_id ? `/calendar?quote=1&customer=${a.customer_id}` : '/calendar?quote=1')}
                      className="press flex-1 py-2 rounded-lg bg-brand-600 text-black text-xs font-bold">Book visit</button>
                  )}
                  <button onClick={() => navigate(`/texts/${a.phone}?ask=${a.id}`)}
                    className={`press flex-1 py-2 rounded-lg text-xs font-bold ${a.kind === 'quote' ? 'border border-gray-300 bg-white text-gray-800' : 'bg-brand-600 text-black'}`}>Draft reply</button>
                  <button onClick={() => askDone(a)} disabled={askHandled.isLoading}
                    className="press flex-1 py-2 rounded-lg border border-gray-300 bg-white text-xs font-bold text-gray-800 disabled:opacity-50">Handled</button>
                </div>
              </li>
            ))}
            {openAsks.length > 8 && <li className="text-xs text-gray-500">{openAsks.length - 8} more on <Link to="/attention" className="underline">Needs Attention</Link></li>}
          </ul>
        </Box>
      )}

      {/* How the crew is doing */}
      <Box title="How the crew is doing" icon={Play} right={
        <span className="text-xs font-semibold text-gray-500">{crew.done ?? 0}/{crew.total ?? 0} done</span>
      }>
        {(crew.routes || []).length === 0 ? (
          <p className="text-sm text-gray-500">No route running today.</p>
        ) : (
          <ul className="space-y-2">
            {(crew.routes || []).map((r: any) => (
              <li key={r.id}>
                <Link to={`/routes?id=${r.id}`} className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{r.name}</p>
                    <p className="text-xs text-gray-500">
                      {r.done}/{r.stops} stops{(r.people || []).length ? ` · ${(r.people || []).join(', ')}` : ' · nobody assigned'}
                    </p>
                  </div>
                  <div className="w-24 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                    <div className="h-full bg-brand-600" style={{ width: `${r.stops ? Math.round((r.done / r.stops) * 100) : 0}%` }} />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-3 pt-3 border-t border-gray-100 space-y-1">
          {(crew.started || []).length === 0 ? (
            <p className="text-xs text-gray-500">Nobody has started their day yet.</p>
          ) : (crew.started || []).map((p: any) => (
            <p key={p.id} className="text-xs text-gray-600">
              <span className="font-semibold text-gray-800">{p.name}</span> started {since(p.started_at)}
              {p.on_break ? ' · on a break' : ''}
            </p>
          ))}
          {(crew.running || []).map((j: any) => (
            <p key={j.id} className="text-xs text-amber-700">Working {j.who} since {since(j.since)}</p>
          ))}
          {crew.last_done && <p className="text-xs text-gray-500">Last finished: {crew.last_done.who} {since(crew.last_done.at)}</p>}
        </div>
      </Box>

      {/* Quotes today (on the one morning page the Field block carries them, with their cards) */}
      {!embedded && <Box title="Quote visits today" icon={FileSignature} right={<Link to="/bids" className="text-xs font-semibold text-brand-700">Open bids</Link>}>
        {(data.quotes || []).length === 0 ? (
          <p className="text-sm text-gray-500">None booked today.</p>
        ) : (
          <ul className="space-y-2">
            {(data.quotes || []).map((q: any) => (
              <li key={q.id} className="rounded-xl border border-gray-200 px-3.5 py-3">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900">
                      {q.at ? `${clock(q.at)}${q.firm ? '' : ' (window)'} · ` : ''}{q.who}
                    </p>
                    {q.where && <p className="text-xs text-gray-500">{q.where}</p>}
                    {q.notes && <p className="text-xs text-gray-500 mt-0.5">{q.notes}</p>}
                  </div>
                  {q.status === 'complete' && <span className="text-[11px] font-semibold text-emerald-700">done</span>}
                </div>
                {q.phone && (
                  <div className="mt-2 flex gap-2">
                    <a href={`tel:${q.phone}`} className="flex-1 py-2 rounded-lg border border-gray-300 text-xs font-semibold text-gray-700 flex items-center justify-center gap-1.5"><Phone className="h-3.5 w-3.5" /> Call</a>
                    <Link to={textsPath(q.phone)} className="flex-1 py-2 rounded-lg border border-gray-300 text-xs font-semibold text-gray-700 flex items-center justify-center gap-1.5"><MessageSquare className="h-3.5 w-3.5" /> Text</Link>
                    <Link to={`/quote-visits/${q.id}`} className="flex-1 py-2 rounded-lg border border-gray-300 text-xs font-semibold text-gray-700 flex items-center justify-center">Open</Link>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Box>}

      {/* Extra work that is not a mow */}
      {(data.extras || []).length > 0 && (
        <Box title="Extra work today" icon={ListChecks}>
          <ul className="divide-y divide-gray-100">
            {(data.extras || []).map((e: any) => (
              <li key={e.id} className="py-2 first:pt-0 last:pb-0">
                <Link to={`/jobs/${e.id}`} className="flex items-center gap-3">
                  <span className="flex-1 text-sm text-gray-800 truncate">{e.title}{e.who ? ` — ${e.who}` : ''}</span>
                  <span className="text-xs text-gray-500">{e.status === 'complete' ? 'done' : e.status}</span>
                  <ChevronRight className="h-4 w-4 text-gray-300" />
                </Link>
              </li>
            ))}
          </ul>
        </Box>
      )}

      {/* What to pack */}
      {pack.length > 0 && (
        <Box title="Pack for today" icon={Backpack}>
          <ul className="space-y-1.5">
            {pack.map((p: any) => (
              <li key={p.kit} className="text-sm text-gray-800">
                <span className="font-semibold">{p.kit}</span>
                <span className="text-gray-500"> — {p.why}</span>
              </li>
            ))}
          </ul>
        </Box>
      )}

      {/* The yard walk before the truck leaves — one item at a time, on its own page (and its own block on the one morning page) */}
      {!embedded && <Box title="Manager check" icon={ClipboardCheck} right={<Link to="/check" className="text-xs font-semibold text-brand-700">Open</Link>}>
        {data.check ? (
          <Link to="/check" className="flex items-center gap-3">
            <span className={`text-lg font-bold tabular-nums ${data.check.status === 'complete' ? (data.check.summary?.release === 'YES' ? 'text-emerald-700' : 'text-red-700') : 'text-gray-900'}`}>
              {data.check.status === 'complete' ? `Release: ${data.check.summary?.release}` : `${data.check.progress.answered}/${data.check.progress.total}`}
            </span>
            <span className="flex-1 min-w-0 text-sm text-gray-600 truncate">
              {data.check.status === 'complete'
                ? (data.check.holds?.length ? `${data.check.holds.length} on hold — ${data.check.holds.map((h: any) => h.label).join(', ')}` : 'Morning check clear')
                : data.check.progress.answered === 0 ? 'Morning check not started' : `Next: ${data.check.next?.label || ''}`}
            </span>
            <ChevronRight className="h-4 w-4 text-gray-300" />
          </Link>
        ) : <Link to="/check" className="text-sm text-brand-700 font-medium">Start the morning check</Link>}
      </Box>}

      {/* Reach out today — reminders whose date has come (and what is coming) */}
      <Box title="Reach out today" icon={Bell} right={<span className="text-xs font-semibold text-gray-500">{(data.reminders?.due || []).length ? `${data.reminders.due.length} due` : 'clear'}</span>}>
        {(data.reminders?.due || []).length === 0 && (data.reminders?.soon || []).length === 0 ? (
          <p className="text-sm text-gray-500 mb-3">Nothing due. Add one below and it comes back on the day.</p>
        ) : (
          <ul className="divide-y divide-gray-100 mb-3">
            {(data.reminders?.due || []).map((r: any) => (
              <li key={r.id} className="py-2 first:pt-0 flex items-center gap-2">
                {/* The crew's winter-upsell photos behind this reminder — every one, each opening full size. */}
                {(r.photo_urls?.length ? r.photo_urls : r.photo_url ? [r.photo_url] : []).map((u: string, i: number) => (
                  <a key={i} href={`${SERVER_ORIGIN}${u}`} target="_blank" rel="noreferrer" className="flex-shrink-0"><img src={`${SERVER_ORIGIN}${u}`} alt="" className="h-10 w-10 rounded-lg object-cover border border-gray-200" /></a>
                ))}
                <button onClick={() => navigate(`/customers?profile=${r.customer_id}`)} className="flex-1 min-w-0 text-left">
                  <span className="block text-sm text-gray-900 truncate">{r.title}</span>
                  <span className={`block text-xs ${r.overdue ? 'text-amber-700' : 'text-gray-500'}`}>{r.due_words}{r.phone ? ` · ${r.phone}` : ''}</span>
                </button>
                {r.phone && <Link to={textsPath(r.phone)} className="text-xs font-semibold text-brand-700 px-2 py-1 rounded-lg border border-brand-200">Text</Link>}
                <button onClick={() => reminderDone.mutate(r.id)} className="text-xs font-semibold text-emerald-700 px-2 py-1 rounded-lg border border-emerald-200">Done</button>
                <button onClick={() => reminderSnooze.mutate(r.id)} className="text-xs text-gray-500 px-2 py-1 rounded-lg border border-gray-200">+1 wk</button>
              </li>
            ))}
            {(data.reminders?.soon || []).map((r: any) => (
              <li key={`soon-${r.id}`} className="py-1.5 flex items-center gap-2 text-xs text-gray-500">
                <span className="flex-1 truncate">{r.title}</span><span>{r.due_words}</span>
              </li>
            ))}
          </ul>
        )}
        <ReminderAdd compact onAdded={() => qc.invalidateQueries('my-day')} />
      </Box>

      {/* The after-mow checklist (the morning one is the manager check now) */}
      {(data.checklists || []).filter((l: any) => l.key !== 'morning').map((list: any) => (
        <Box key={list.key} title={list.label} icon={list.key === 'morning' ? Sun : Moon}
          right={<span className="text-xs font-semibold text-gray-500">{list.left ? `${list.left} left` : 'all done'}</span>}>
          <ul className="space-y-1">
            {list.items.map((i: any) => (
              <li key={i.key}>
                <button onClick={() => tick.mutate({ list: list.key, item: i.key, on: !i.done })}
                  className="w-full flex items-center gap-3 py-2 text-left">
                  <span className={`h-6 w-6 rounded-lg flex items-center justify-center flex-shrink-0 border ${i.done ? 'bg-brand-600 border-brand-600 text-black' : 'border-gray-300 text-transparent'}`}>
                    <Check className="h-4 w-4" />
                  </span>
                  <span className={`text-sm ${i.done ? 'text-gray-500 line-through' : 'text-gray-800'}`}>{i.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </Box>
      ))}

      {/* To-dos: today's (with the client and the address they are for), the ones still open from an earlier
          day, and two ways to add one — a quick line for Needs Attention, or the full sheet with a client and
          an address (components/TodoSheet). */}
      <Box title="To-dos today" icon={ListChecks} right={<button onClick={() => setTodoSheet(true)} className="text-xs font-semibold text-brand-700">+ To-do at an address</button>}>
        {(data.todos || []).length === 0 && (data.todos_open || []).length === 0 ? (
          <p className="text-sm text-gray-500">Nothing on the calendar.</p>
        ) : (
          <ul className="divide-y divide-gray-100 mb-3">
            {(data.todos || []).map((t: any) => (
              <li key={t.id} className="py-2 first:pt-0 flex items-center gap-2">
                <Link to={`/jobs/${t.id}`} className="flex-1 min-w-0 flex items-center gap-3">
                  <span className="flex-1 min-w-0">
                    <span className={`block text-sm truncate ${t.status === 'complete' ? 'text-gray-500 line-through' : 'text-gray-800'}`}>{t.at ? `${clock(t.at)} · ` : ''}{t.title}</span>
                    {(t.who || t.where || t.route) && <span className="block text-xs text-gray-500 truncate">{[t.who, t.where, t.route ? `on ${t.route}'s day` : null].filter(Boolean).join(' · ')}</span>}
                  </span>
                  <ChevronRight className="h-4 w-4 text-gray-300 flex-shrink-0" />
                </Link>
                {t.status !== 'complete' && (
                  <button onClick={() => todoDone.mutate(t.id)} disabled={todoDone.isLoading} className="text-xs font-semibold text-emerald-700 px-2 py-1 rounded-lg border border-emerald-200 flex-shrink-0">Done</button>
                )}
              </li>
            ))}
            {(data.todos_open || []).map((t: any) => (
              <li key={`o${t.id}`} className="py-2 flex items-center gap-2">
                <Link to={t.href || `/jobs/${t.id}`} className="flex-1 min-w-0 flex items-center gap-3">
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm text-gray-800 truncate">{t.what}</span>
                    <span className="block text-xs text-amber-700 truncate">Still open from {t.day_words}{[t.customer_name, t.street].filter(Boolean).length ? ` · ${[t.customer_name, t.street].filter(Boolean).join(' · ')}` : ''}</span>
                  </span>
                  <ChevronRight className="h-4 w-4 text-gray-300 flex-shrink-0" />
                </Link>
                <button onClick={() => todoDone.mutate(t.id)} disabled={todoDone.isLoading} className="text-xs font-semibold text-emerald-700 px-2 py-1 rounded-lg border border-emerald-200 flex-shrink-0">Done</button>
              </li>
            ))}
          </ul>
        )}
        <TodoSheet open={todoSheet} onClose={() => setTodoSheet(false)} onAdded={() => { setTodoSheet(false); qc.invalidateQueries('my-day'); qc.invalidateQueries('my-day-attention'); }} />
        <div className="flex gap-2">
          <input value={todoText} onChange={e => setTodoText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && todoText.trim()) addTodo.mutate(todoText.trim()); }}
            placeholder="Type a to-do and hit enter"
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2.5 text-sm" />
          <button onClick={() => todoText.trim() && addTodo.mutate(todoText.trim())} disabled={addTodo.isLoading || !todoText.trim()}
            className="press px-4 rounded-xl bg-brand-600 text-black text-sm font-semibold disabled:opacity-50">Add</button>
        </div>
        <p className="mt-1.5 text-[11px] text-gray-500">Goes on Needs Attention, not the calendar.</p>
      </Box>
    </div>
  );
}
